<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import {
  BAlert,
  BButton,
  BCollapse,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useQuery } from '@vue/apollo-composable';
import { useVisibleQueryPolling } from '@/composables/visible-query-polling';
import {
  UploadManagerTransferStartsDocument,
  UploadManagerUserDocument,
} from '@/gql/graphql';
import UploadComponent from '@/components/upload/UploadComponent.vue';
import ComputeTaskList from '@/components/compute/ComputeTaskList.vue';
import WebUploadList from '@/components/upload/WebUploadList.vue';
import { scrollToElement } from '@/utils/view-utils';
import { formatDateTimeToIso } from '@/utils/date-utils';
import {
  getStartingTransferPlaceholder,
  TRANSFER_TASK_TYPES,
} from '@/utils/transfer-start-placeholder';
import {
  COMPUTE_TASK_TYPES,
  SHARING_USER_ROLES,
  UPLOAD_MODES,
  UploadModeType,
} from '@/constants';
import { useRouter } from 'vue-router';

const router = useRouter();

const showUploadOptions = ref(false);
const showWebUploadStarted = ref(false);
const showDataTransferStarted = ref(false);
const selectedUploadOption = ref<UploadModeType>(UPLOAD_MODES.WEB);

// --- Query User ---
const {
  result: resultUserDetails,
  loading: loadingUserDetails,
  error: errorFetchUserDetails,
  refetch: refetchUserDetails,
} = useQuery(UploadManagerUserDocument);

// --- User Organization ---
const userOrganization = computed(() => {
  const assocOrgs =
    resultUserDetails.value?.user?.organizationsAssociated || [];
  const org = assocOrgs
    .filter((assoc) => assoc && assoc.organization)
    .find((assoc2) => assoc2?.removedAt === null)?.organization;

  return org;
});
const userHasNoOrg = computed(() => {
  return !userOrganization.value;
});

// --- Query recent Compute Pipeline parents and their transfer children ---
const tasksQueryVariables = computed(() => {
  return {
    userRole: SHARING_USER_ROLES.OWNER,
    taskTypes: [COMPUTE_TASK_TYPES.COMPUTE_PIPELINE],
    createdAfter: formatDateTimeToIso(
      new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    ),
    pagingPageCurrent: 1,
    pagingItemsPerPage: 5,
  };
});

const {
  result: tasksQueryResult,
  error: tasksQueryError,
  refetch: refetchTasks,
  onResult: onTasksQueryDone,
  onError: onTasksQueryError,
  query: taskPollingQuery,
} = useQuery(UploadManagerTransferStartsDocument, tasksQueryVariables.value);
useVisibleQueryPolling(taskPollingQuery);

onTasksQueryDone((res) => {
  console.debug('Compute Pipeline Tasks response retrieved');
  if (res.data) {
    if (res.data.computeTasks) {
      console.debug('Compute Pipeline Tasks retrieved successfuly');
    } else {
      console.error(
        'Error, could not retrieve compute pipeline tasks:',
        res.data
      );
    }
  } else if (!res.loading) {
    console.error(
      'Error, could not retrieve compute pipeline tasks result data:',
      res
    );
  }
});

onTasksQueryError((err) => {
  console.error(
    'Error fetching compute pipeline tasks (onTasksQueryError):',
    err.message
  );
});

function requestRefetchTasks() {
  // refetch the tasks with the current filters
  refetchTasks(tasksQueryVariables.value)
    ?.catch((err) => {
      console.error('Refetch tasks failed:', err);
    })
    .then(() => {
      console.debug('Refetched tasks');
    });
}

const startingTasksPlaceholder = computed<string | null>(() => {
  // A failed poll keeps the last result, so its count may be outdated.
  if (tasksQueryError.value) return null;
  return getStartingTransferPlaceholder(
    tasksQueryResult.value?.computeTasks?.computeTasks || []
  );
});

// The alert has its own state, so dismissing it keeps the poll error, and
// with it the hidden count, until a poll succeeds.
const showTasksQueryError = ref(false);
watch(tasksQueryError, (err) => {
  showTasksQueryError.value = !!err;
});

// --- Handlers ---
function uploadOptionSelected(option: UploadModeType) {
  // If web upload already selected, just toggle the visibility
  if (selectedUploadOption.value === option) {
    showUploadOptions.value = !showUploadOptions.value;
    return;
  }
  // Otherwise, set the upload mode to WEB and show the options
  selectedUploadOption.value = option;
  showUploadOptions.value = true;
}

function requestRefetchUserDetails() {
  refetchUserDetails()
    ?.catch((err) => {
      console.error('Refetch user failed:', err);
    })
    ?.then(() => {
      console.debug('Refetched user');
    });
}

function onStartedDataTransfer() {
  requestRefetchTasks();

  // collapse the UploadComponent
  showUploadOptions.value = false;

  // show the info message
  showDataTransferStarted.value = true;

  // scroll to ftp transfers list with delay to allow the UploadComponent to collapse
  scrollToElement('heading-data-transfer-list', 500);

  // hide the info message after 5 seconds
  setTimeout(() => {
    showDataTransferStarted.value = false;
  }, 5000);
}
function onStartedWebUpload() {
  // collapse the UploadComponent
  showUploadOptions.value = false;

  // show the info message
  showWebUploadStarted.value = true;

  // scroll to web uploads list with delay to allow the UploadComponent to collapse
  scrollToElement('heading-web-upload-list', 500);

  // hide the info message after 5 seconds
  setTimeout(() => {
    showWebUploadStarted.value = false;
  }, 5000);
}
// onMounted check existing URL parameter for base-space-transfer equals start
onMounted(() => {
  const query = router.currentRoute.value.query;
  if (query['base-space-transfer'] === 'start') {
    onStartedDataTransfer();
  }
});
</script>

<template>
  <div>
    <!-- Page Header -->
    <h1><font-awesome-icon :icon="['fas', 'upload']" /> Upload Manager</h1>
    <h5>Track Your Data Uploads</h5>
    <hr />
    <p>
      Use this view to monitor the progress of your data transfers and uploads.
      We support uploading data in <strong>FASTQ</strong> formats either
      directly from your computer or via FTP directly from your sequencing
      provider.
    </p>
    <p>
      <strong>Tip:</strong> Keep your files compressed using
      <code>gzip</code> to reduce bandwidth usage and expedite the upload
      process.
    </p>

    <!-- Error Fetching User Details -->
    <BAlert v-model="errorFetchUserDetails" variant="danger">
      {{ errorFetchUserDetails }}
      <b-button
        variant="outline-danger"
        @click="requestRefetchUserDetails"
        size="sm"
      >
        <font-awesome-icon icon="sync" />
        Retry
      </b-button>
    </BAlert>

    <!-- New Upload -->
    <div
      v-if="loadingUserDetails"
      class="centered text-center"
      style="min-height: 200px"
    >
      <BSpinner label="Loading user details" />
    </div>
    <!-- No organization - Cannot Upload Data -->
    <div v-if="!userOrganization && !loadingUserDetails">
      <b-alert v-model="userHasNoOrg" variant="warning">
        You are not associated with any organization. Please contact your
        account manager or organization administrator to get access to the
        upload features.
      </b-alert>
    </div>
    <!-- Buttons and Collapsible Upload Component -->
    <div v-if="userOrganization">
      <!-- WEB Upload Buttons -->
      <b-button
        @click="uploadOptionSelected(UPLOAD_MODES.WEB)"
        :pressed="
          showUploadOptions && selectedUploadOption === UPLOAD_MODES.WEB
        "
        id="new-upload-web"
        variant="outline-dark"
        class="mb-2 me-2"
      >
        <font-awesome-icon :icon="['fas', 'upload']" class="me-1" />
        Upload New Data
      </b-button>
      <!-- FTP Transfer Button -->
      <b-button
        @click="uploadOptionSelected(UPLOAD_MODES.FTP)"
        :pressed="
          showUploadOptions && selectedUploadOption === UPLOAD_MODES.FTP
        "
        id="new-upload-ftp"
        variant="outline-dark"
        class="mb-2 me-2"
      >
        <font-awesome-icon :icon="['fas', 'server']" class="me-1" />
        FTP Server
      </b-button>
      <!-- Wasabi Transfer Button -->
      <b-button
        @click="uploadOptionSelected(UPLOAD_MODES.WASABI)"
        :pressed="
          showUploadOptions && selectedUploadOption === UPLOAD_MODES.WASABI
        "
        id="new-upload-wasabi"
        variant="outline-dark"
        class="mb-2 me-2"
      >
        <font-awesome-icon :icon="['fas', 'cloud-arrow-down']" class="me-1" />
        Wasabi S3
      </b-button>
      <!-- Link to BaseSpace -->
      <router-link :to="{ name: 'BaseSpaceView' }">
        <b-button
          variant="outline-dark"
          class="mb-2 me-2"
          id="connect-basespace-button"
        >
          <font-awesome-icon icon="info-circle" class="me-1" />
          Illumina BaseSpace
        </b-button>
        <b-tooltip target="connect-basespace-button">
          Connect to Illumina BaseSpace Sequence Hub to upload data directly
          from your projects.
        </b-tooltip>
      </router-link>
      <!-- Collapsible Upload Component -->
      <b-collapse v-model="showUploadOptions" id="new-upload">
        <UploadComponent
          :selected-upload-option="selectedUploadOption"
          @started-data-transfer="onStartedDataTransfer"
          @started-web-uploads="onStartedWebUpload"
        />
      </b-collapse>
    </div>

    <!-- Active Data Transfers Section -->
    <h5 id="heading-data-transfer-list" class="mt-4">Data Transfer Tasks</h5>
    <p>
      Once initiated, the file transfer continues automatically. You can safely
      navigate away without interrupting it, and will continue even if you leave
      or close the app.
    </p>
    <b-alert v-model="showDataTransferStarted" variant="info">
      Data transfer registered and started successfully.
      <font-awesome-icon icon="check" />
    </b-alert>

    <!-- List starting data transfer tasks based on inspection of compute pipeline tasks -->
    <b-alert v-model="showTasksQueryError" variant="danger" dismissible>
      {{ tasksQueryError }}
    </b-alert>

    <!-- List transfer children, paging included -->
    <ComputeTaskList
      parent-name="Upload Manager"
      :placeholder="startingTasksPlaceholder"
      :user-role="SHARING_USER_ROLES.OWNER"
      :task-types="TRANSFER_TASK_TYPES"
      task-name-column-title="Transfer"
    />

    <!-- Active WEB Uploads Section -->
    <h5 id="heading-web-upload-list" class="mt-4">Active Direct Uploads</h5>
    <p>
      Your file is uploaded directly from this browser session. You may continue
      browsing data, samples, projects, and analysis. However, avoid closing or
      refreshing the browser during the upload. If interrupted, your progress is
      saved so you can resume the upload without losing data within 7 days.
    </p>
    <b-alert v-model="showWebUploadStarted" variant="info">
      Upload registered and started successfully.
      <font-awesome-icon icon="check" />
    </b-alert>
    <WebUploadList />

    <!-- End of Content -->
  </div>
</template>

<style scoped></style>
